/**
 * Type-safe ID validation and comparison utilities
 *
 * Two types of IDs in the system:
 * 1. ObjectIdString - MongoDB ObjectId (24 hex characters), generated by backend
 * 2. LocalId - Numeric ID used locally before POST to backend
 *
 * Use the appropriate validator based on what you expect:
 * - isObjectIdString() for backend-generated _id
 * - isLocalId() for local numeric IDs before POST
 */

// =============================================================================
// ObjectIdString - Backend-generated MongoDB ObjectId
// =============================================================================

/**
 * Branded type for MongoDB ObjectId strings.
 * Ensures type safety when working with backend-generated IDs.
 */
export type ObjectIdString = string & {
  readonly __brand: 'ObjectIdString';
};

/** MongoDB ObjectId string length (24 hexadecimal characters). */
export const OBJECT_ID_LENGTH = 24;

/**
 * Regex for MongoDB ObjectId validation (24 hex characters)
 * This is equivalent to what bson/mongodb libraries check internally.
 * Repeat count must stay aligned with {@link OBJECT_ID_LENGTH}.
 */
const OBJECT_ID_REGEX = /^[a-f\d]{24}$/i;

const objectIdLengthInRegex = Number(
  OBJECT_ID_REGEX.source.match(/\{(\d+)\}\$/)?.[1]
);
if (objectIdLengthInRegex !== OBJECT_ID_LENGTH) {
  throw new Error(
    `OBJECT_ID_REGEX repeat count (${objectIdLengthInRegex}) must match OBJECT_ID_LENGTH (${OBJECT_ID_LENGTH})`
  );
}

/**
 * Type guard for MongoDB ObjectId strings.
 * ObjectId is always a 24-character hexadecimal string.
 *
 * @param input - Value to check
 * @returns true if input is a valid ObjectId string
 *
 * @example
 * isObjectIdString('507f1f77bcf86cd799439011') // true
 * isObjectIdString('123') // false - not 24 chars
 * isObjectIdString(' valid ') // false - not hex
 * isObjectIdString(123) // false - not a string
 */
export function isObjectIdString(input: unknown): input is ObjectIdString {
  return typeof input === 'string' && OBJECT_ID_REGEX.test(input);
}

// =============================================================================
// LocalId - Pre-POST numeric IDs
// =============================================================================

/**
 * Branded type for local numeric IDs.
 * Used for entities before they are POSTed to backend.
 */
export type LocalId = number & {
  readonly __brand: 'LocalId';
};

/**
 * Type guard for local numeric IDs.
 * LocalId is a non-NaN, finite number.
 *
 * @param input - Value to check
 * @returns true if input is a valid local ID
 *
 * @example
 * isLocalId(123) // true
 * isLocalId(0) // true
 * isLocalId(-1) // true (negative can be valid for temp IDs)
 * isLocalId('123') // false - must be number
 * isLocalId(NaN) // false
 * isLocalId(Infinity) // false
 */
export function isLocalId(input: unknown): input is LocalId {
  return typeof input === 'number' && Number.isFinite(input);
}

/**
 * Parses a value to LocalId if possible.
 * Handles the case where LocalId might be stored as string.
 *
 * @param input - Value to parse
 * @returns LocalId or null if not parseable
 */
export function parseLocalId(input: unknown): LocalId | null {
  if (isLocalId(input)) return input;
  if (typeof input === 'string') {
    const parsed = Number(input);
    if (Number.isFinite(parsed)) return parsed as LocalId;
  }
  return null;
}

// =============================================================================
// Comparison utilities
// =============================================================================

/**
 * Compares two ObjectIdStrings for equality.
 * Handles null/undefined gracefully.
 *
 * @example
 * areObjectIdsEqual('507f1f77bcf86cd799439011', '507f1f77bcf86cd799439011') // true
 * areObjectIdsEqual(null, null) // true (both "no ID")
 * areObjectIdsEqual('507f1f77bcf86cd799439011', null) // false
 */
export function areObjectIdsEqual(
  a: string | null | undefined,
  b: string | null | undefined
): boolean {
  const aValid = isObjectIdString(a);
  const bValid = isObjectIdString(b);

  if (!aValid && !bValid) return true;
  if (aValid !== bValid) return false;
  return a === b;
}

/**
 * Compares two LocalIds for equality.
 * Handles null/undefined gracefully.
 */
export function areLocalIdsEqual(
  a: number | null | undefined,
  b: number | null | undefined
): boolean {
  const aValid = isLocalId(a);
  const bValid = isLocalId(b);

  if (!aValid && !bValid) return true;
  if (aValid !== bValid) return false;
  return a === b;
}

/**
 * Checks if ObjectId has changed from one valid value to another.
 * Ignores transitions from/to invalid values (undefined, null).
 *
 * @example
 * hasObjectIdChanged(undefined, '507f1f77bcf86cd799439011') // false (initialization)
 * hasObjectIdChanged('507f1f77bcf86cd799439011', '617f1f77bcf86cd799439022') // true (change!)
 */
export function hasObjectIdChanged(
  previousId: string | null | undefined,
  currentId: string | null | undefined
): boolean {
  const prevValid = isObjectIdString(previousId);
  const currValid = isObjectIdString(currentId);
  return prevValid && currValid && previousId !== currentId;
}

export function getObjectIdMapValue<V>(
  map: Map<string, V>,
  key: string
): V | undefined {
  if (map.has(key)) {
    return map.get(key);
  }

  // areObjectIdsEqual treats two invalid ids as equal (both "no id");
  // only run canonical ObjectId lookup for valid keys.
  if (!isObjectIdString(key)) {
    return undefined;
  }

  for (const [mapKey, value] of map) {
    if (areObjectIdsEqual(mapKey, key)) {
      return value;
    }
  }

  return undefined;
}

export function hasObjectIdMapValue(
  map: Map<string, unknown>,
  key: string
): boolean {
  return getObjectIdMapValue(map, key) !== undefined;
}

